import type { Rng } from '../rng';
import { LUCK_RTP, luckOf } from './luck';
import type { ActResult, CasinoGame, TableEnv, TableOption } from './types';

/**
 * Crash: a multiplier climbs from 1.00x until it crashes. Cash out before
 * it does and you win bet x multiplier; stay too long and you lose the bet.
 *
 * The crash point is drawn the moment the round starts, with
 * P(crash >= m) = (1 - EDGE) / m — so whatever you aim for, the expected
 * return is (1 - EDGE). About EDGE of rounds crash instantly at 1.00x.
 * Checked in odds.test.ts.
 *
 * The lens updates a few times a second and a ring tap takes a moment to
 * arrive, so the optional auto cash-out is the reliable way to hit a
 * target. A manual cash-out pays the multiplier at the instant the tap is
 * processed, which is never less than what the lens was showing.
 */
// 4% measured ~94.7% as played (cash-outs land on the tick); 2.7% lifts
// it to ~96% (balance patch, 2026-09-23).
export const EDGE = 0.027;
/** The ★'s luck: the edge turns negative, so the rocket flies further and
 *  every cash-out target from x1.03 up returns LUCK_RTP (103%, or 110%
 *  with Liquid Courage). */
export function luckEdge(luck: number): number {
  return luck > 0 ? 1 - LUCK_RTP[Math.min(luck, LUCK_RTP.length - 1)]! : EDGE;
}
/** Cashing out at or above this wins the floor jackpot. */
export const JACKPOT_MULTIPLIER = 50;
/** Growth rate: the multiplier doubles about every 6 seconds. */
const GROWTH_PER_MS = Math.LN2 / 6000;
const MAX_MULTIPLIER = 1000;
/** A cash-out tap that arrives just after a crash must not launch the next
 *  round, so "Launch again" ignores taps this soon after a round ends. */
const LAUNCH_COOLDOWN_MS = 1200;

export const AUTO_TARGETS: (number | null)[] = [null, 1.5, 2, 3, 5, 10, 25, 50];

export function drawCrashPoint(rng: Rng, edge: number = EDGE): number {
  const u = rng.next();
  const raw = (1 - edge) / (1 - u);
  if (raw < 1) return 1;
  return Math.min(MAX_MULTIPLIER, Math.floor(raw * 100) / 100);
}

export function multiplierAt(elapsedMs: number): number {
  return Math.floor(Math.exp(GROWTH_PER_MS * Math.max(0, elapsedMs)) * 100) / 100;
}

export interface CrashState {
  phase: 'idle' | 'flying' | 'done';
  crashPoint: number;
  startedAt: number;
  bet: number;
  autoIndex: number;
  /** Multiplier currently on the lens (only changes trigger a redraw). */
  shown: number;
  /** Where the player cashed out, or null if the round crashed on them. */
  cashedAt: number | null;
  returned: number;
  /** When the last round ended — see LAUNCH_COOLDOWN_MS. */
  endedAt: number;
  /** Recent crash points, newest first. */
  history: number[];
  /** Explosion frame showing after a crash (1..BOOM_FRAMES), 0 when done. */
  boom?: number;
}

/** The explosion that plays when the rocket crashes on you: a pop at the
 *  tip, a burst, BOOM, then debris — then the finished chart. Each frame is
 *  5 rows (the chart's height) and inside the 25-character pane. */
export const BOOM_FRAMES: readonly string[][] = [
  ['                  \\|/', '         ....     -*-', '     ....         /|\\', '  ...', ''],
  ["           ' \\ | / '", '         .  --  *  --  .', "           . / | \\ .", '', ''],
  ['', '', '       * . BOOM! . *', '', ''],
  ["        .    '     .", "     '    .     ,", "          .   '", '   ,          .', ''],
];

function fmt(m: number): string {
  return `${m.toFixed(2)}x`;
}

/** Chart size on the lens: 5 rows by up to 20 columns (inside 25). */
const CHART_ROWS = 5;
const CHART_COLS = 20;
/** The chart grows a column per this much flight, up to CHART_COLS, then
 *  compresses to fit (like a real crash chart's time axis). */
const MS_PER_COL = 250;

/**
 * The flight so far as a line chart: time left to right, the multiplier
 * bottom to top, rescaled so the tip is always the top row. The curve is
 * the real one (it bends upward, faster and faster). `end` is the rocket
 * (^ in flight, X when it crashed); `cashedAt` marks where you got out ($).
 */
export function crashChart(top: number, end: '^' | 'X', cashedAt: number | null = null, rows: number = CHART_ROWS): string[] {
  const elapsed = Math.log(Math.max(1, top)) / GROWTH_PER_MS;
  const cols = Math.max(1, Math.min(CHART_COLS, 1 + Math.floor(elapsed / MS_PER_COL)));
  const grid = Array.from({ length: rows }, () => Array<string>(cols).fill(' '));
  const height = (m: number) => (top > 1 ? ((m - 1) / (top - 1)) * (rows - 1) : 0);
  let cashCol = -1;
  for (let c = 0; c < cols; c++) {
    const m = cols === 1 ? top : Math.exp(GROWTH_PER_MS * ((elapsed * c) / (cols - 1)));
    if (cashedAt !== null && cashCol < 0 && m >= cashedAt) cashCol = c;
    grid[rows - 1 - Math.round(height(m))]![c] = c === cols - 1 ? end : '.';
  }
  if (cashCol >= 0 && cashCol < cols - 1) grid[rows - 1 - Math.round(height(cashedAt!))]![cashCol] = '$';
  return grid.map((row) => `  ${row.join('')}`.trimEnd());
}

function netLabel(net: number): string {
  return net > 0 ? `+$${net}` : net === 0 ? 'Broke even' : `-$${-net}`;
}

/** Rounded to whole dollars (nearest, so rounding doesn't lean either way). */
function payout(bet: number, multiplier: number): number {
  return Math.round(bet * multiplier);
}

function finish(state: CrashState, bankroll: number, cashedAt: number | null, now: number): ActResult<CrashState> {
  const returned = cashedAt === null ? 0 : payout(state.bet, cashedAt);
  return {
    state: {
      ...state,
      phase: 'done',
      endedAt: now,
      shown: cashedAt ?? state.crashPoint,
      cashedAt,
      returned,
      // Crashed on you: the explosion plays before the chart (see tick).
      boom: cashedAt === null ? 1 : 0,
      history: [state.crashPoint, ...state.history].slice(0, 4),
    },
    bankroll: bankroll + returned,
    settled: { wagered: state.bet, returned },
    jackpot: cashedAt !== null && cashedAt >= JACKPOT_MULTIPLIER,
  };
}

/** Where the round stands right now: crashed, auto-cashed, or still flying. */
function resolveNow(state: CrashState, env: TableEnv): ActResult<CrashState> | null {
  const current = multiplierAt(env.now - state.startedAt);
  const auto = AUTO_TARGETS[state.autoIndex] ?? null;
  if (auto !== null && auto < state.crashPoint && current >= auto) return finish(state, env.bankroll, auto, env.now);
  if (current >= state.crashPoint) return finish(state, env.bankroll, null, env.now);
  if (current === state.shown) return null;
  return { state: { ...state, shown: current }, bankroll: env.bankroll };
}

export const crash: CasinoGame<CrashState> = {
  id: 'crash',
  noDealer: true,
  usesLuck: true,
  name: 'Crash',
  blurb: 'Cash out before the multiplier crashes.',
  baseMinBet: 5,
  baseMaxBet: 100,
  lockedHint: '• cash out',
  specialPerk: 'Luck: the rocket flies further (103% back).',
  jackpotRule: 'Cash out at 50x or higher.',
  create: () => ({
    phase: 'idle',
    crashPoint: 1,
    startedAt: 0,
    bet: 0,
    autoIndex: 0,
    shown: 1,
    cashedAt: null,
    returned: 0,
    endedAt: 0,
    history: [],
  }),
  locked: (state) => state.phase === 'flying',
  options(state): TableOption[] {
    if (state.phase === 'flying') return [{ id: 'cashout', label: `CASH OUT $${payout(state.bet, state.shown)}` }];
    const auto = AUTO_TARGETS[state.autoIndex] ?? null;
    return [
      { id: 'launch', label: state.phase === 'done' ? 'Launch again' : 'Launch' },
      { id: 'auto', label: `Auto: ${auto === null ? 'off' : fmt(auto)}` },
    ];
  },
  describe(state, _env, highlighted) {
    // Three at most: "Last: 123.45 12.34 1.00" stays inside the 25-character pane.
    const history = state.history.length ? `Last: ${state.history.slice(0, 3).map((m) => m.toFixed(2)).join(' ')}` : '';
    if (highlighted === 'auto') {
      return 'Auto cash-out takes your winnings the moment the multiplier reaches the target.\n\nTap to change it.';
    }
    if (state.phase === 'flying') {
      const auto = AUTO_TARGETS[state.autoIndex] ?? null;
      return [`${fmt(state.shown)}${auto === null ? '' : `   auto ${fmt(auto)}`}`, ...crashChart(state.shown, '^')].join('\n');
    }
    if (state.phase === 'done') {
      // The chart runs on to where it crashed, so you see what you left, and
      // a cash-out also names the crash point (the chart drops to 4 rows to
      // make room within the pane).
      const result =
        state.cashedAt === null
          ? [`Crashed ${fmt(state.crashPoint)} -$${state.bet}`]
          : [`Out at ${fmt(state.cashedAt)} ${netLabel(state.returned - state.bet)}`, `Crashed at ${fmt(state.crashPoint)}`];
      if (state.boom) return [...result, ...BOOM_FRAMES[state.boom - 1]!].join('\n').trimEnd();
      const rows = state.cashedAt === null ? CHART_ROWS : CHART_ROWS - 1;
      return [...result, ...crashChart(state.crashPoint, 'X', state.cashedAt, rows), history].filter(Boolean).join('\n');
    }
    return ['Launch, then tap to cash out before it crashes.', history].filter(Boolean).join('\n\n');
  },
  act(state, optionId, env) {
    if (optionId === 'auto' && state.phase !== 'flying') {
      return { state: { ...state, autoIndex: (state.autoIndex + 1) % AUTO_TARGETS.length }, bankroll: env.bankroll };
    }
    if (optionId === 'launch' && state.phase === 'done' && env.now - state.endedAt < LAUNCH_COOLDOWN_MS) {
      return { state, bankroll: env.bankroll };
    }
    if (optionId === 'launch' && state.phase !== 'flying') {
      const flying: CrashState = {
        ...state,
        phase: 'flying',
        crashPoint: drawCrashPoint(env.rng, luckEdge(luckOf(env))),
        startedAt: env.now,
        bet: env.bet,
        shown: 1,
        cashedAt: null,
        returned: 0,
        boom: 0,
      };
      const bankroll = env.bankroll - env.bet;
      // A 1.00x crash is over before it starts.
      if (flying.crashPoint <= 1) return finish(flying, bankroll, null, env.now);
      return { state: flying, bankroll };
    }
    if (optionId === 'cashout' && state.phase === 'flying') {
      const settled = resolveNow(state, env);
      if (settled && settled.state.phase === 'done') return settled;
      return finish(state, env.bankroll, multiplierAt(env.now - state.startedAt), env.now);
    }
    return { state, bankroll: env.bankroll };
  },
  tick(state, env) {
    // After a crash, show the next explosion frame each tick (so none is
    // skipped however often the lens refreshes), then settle back to the
    // chart. The money was already settled when it crashed.
    if (state.phase === 'done' && state.boom) {
      const boom = state.boom >= BOOM_FRAMES.length ? 0 : state.boom + 1;
      return { state: { ...state, boom }, bankroll: env.bankroll };
    }
    if (state.phase !== 'flying') return null;
    return resolveNow(state, env);
  },
};
